import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ExpandableListProps {
  items: unknown[]
  title?: string
  initialVisible?: number
  renderItem?: (item: unknown, index: number) => React.ReactNode
  compact?: boolean
}

export function ExpandableList({
  items,
  title,
  initialVisible = 5,
  renderItem,
  compact = false,
}: ExpandableListProps) {
  const [expanded, setExpanded] = useState(false)

  if (!items || items.length === 0) return null

  const visibleItems = expanded ? items : items.slice(0, initialVisible)
  const hasMore = items.length > initialVisible
  const hiddenCount = items.length - initialVisible

  // Default render function
  const defaultRender = (item: unknown, index: number) => (
    <li key={index} className="truncate" title={String(item)}>
      {String(item)}
    </li>
  )

  const render = renderItem || defaultRender

  return (
    <div className="space-y-1">
      {title && (
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {title} ({items.length})
        </h4>
      )}
      <ul
        className={cn(
          'list-disc list-inside ml-2',
          compact ? 'text-[11px] space-y-0' : 'text-xs space-y-0.5',
        )}
      >
        {visibleItems.map((item, i) => render(item, i))}
      </ul>

      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            'flex items-center gap-1 ml-2',
            'text-xs text-primary hover:text-primary/80 transition-colors',
            'font-medium',
          )}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Show {hiddenCount} more
            </>
          )}
        </button>
      )}
    </div>
  )
}
